<template>
    <div
        class="md:w-full md:max-w-[400px] absolute left-1 right-1 top-1/2 md:top-36 lg:top-24 md:h-[85vh] md:max-h-[95%] md:left-auto md:right-6 bottom-2 md:bottom-auto rounded-lg border border-slate-200 shadow-lg p-2 bg-white dark:bg-slate-800 z-50"
    >
        <div class="relative h-full flex flex-col">
            <button
                @click="$emit('closeInfoWindow')"
                class="absolute w-8 h-8 bg-white dark:bg-slate-800 rounded-full shadow dark:shadow-slate-900/50 justify-center items-center p-2 mt-2 mr-2 text-slate-500 text-xl flex transition-colors z-10 hover:text-slate-400 hover:border-slate-300 hover:bg-slate-200 active:border-slate-300 active:bg-slate-200 focus:outline-none focus:border-2"
                :class="{
                    'right-2 top-2': mapType !== 'search',
                    'top-1 right-0': mapType === 'search',
                }"
            >
                <i class="fal fa-times"></i>
                <span class="sr-only">Close</span>
            </button>
            <template v-if="mapType === 'search'">
                <div
                    class="w-8 h-8 bg-white dark:bg-slate-800 rounded-full shadow dark:shadow-slate-900/50 justify-center items-center inline-flex p-2 absolute mt-2 mr-2 top-1 right-10"
                >
                    <Fav :property="props.infoWindowContent" :isWidget="true" />
                </div>
                <div
                    class="active:outline-none"
                    :class="{ 'cursor-pointer': mapType !== 'property' }"
                    @click="handleRouteToProperty"
                >
                    <ImageLoader>
                        <template #image>
                            <div
                                class="w-full h-52 bg-center bg-cover bg-no-repeat rounded-md"
                                :data-src="propertyImage"
                                :style="`background-image: url('${propertyImage}')`"
                            ></div>
                        </template>
                        <template #preloader>
                            <div
                                class="w-full h-52 rounded-md bg-slate-200 shimmer"
                            ></div>
                        </template>
                        <template #error>
                            <div
                                class="w-full h-52 flex items-center justify-center rounded-md bg-slate-200 text-slate-400"
                            >
                                <Icon icon="no-image" />
                            </div>
                        </template>
                    </ImageLoader>
                </div>
            </template>

            <div class="overflow-y-auto relative">
                <div
                    v-show="mapType !== 'favorites' && mapType !== 'property'"
                    class="w-full pt-4 pb-2 bg-white dark:bg-slate-800 sticky top-0 z-10 active:outline-none"
                    @click="handleRouteToProperty"
                    :class="{ 'cursor-pointer': mapType !== 'property' }"
                >
                    <h3
                        class="font-semibold text-xl leading-7 text-slate-800 mb-1"
                    >
                        {{ props.infoWindowContent.name }}
                    </h3>
                    <p class="text-slate-500 leading-tight text-sm mb-2">
                        {{ props.infoWindowContent.address_1 }}
                        {{ props.infoWindowContent.locality }}
                        {{ props.infoWindowContent.country }}
                    </p>
                </div>
                <div
                    class="flex flex-col mb-5"
                    v-if="cashPrice && nightStayString"
                >
                    <div class="gap-2 items-center inline-flex">
                        <div
                            class="text-slate-800 text-xl font-medium leading-7"
                        >
                            {{ cashPrice }}
                        </div>
                        <div
                            class="text-slate-500 text-xs font-medium leading-none pt-1"
                        >
                            {{ nightStayString }} total
                        </div>
                    </div>
                    <div class="gap-2 items-center inline-flex">
                        <div
                            class="text-slate-800 text-xl font-medium leading-7"
                        >
                            {{ $filters.formatThousand(pointsPrice) }}
                        </div>
                        <div
                            class="text-slate-500 text-xs font-medium leading-none pt-1"
                        >
                            {{
                                props.infoWindowContent.group.name
                                    ? `${props.infoWindowContent.group.name} points`
                                    : 'Points price'
                            }}
                        </div>
                    </div>
                </div>
                <!-- END PRICE AND STAY INFO -->
                <div
                    class="flex items-center mb-2"
                    v-show="mapType === 'search'"
                >
                    <h5
                        class="grow shrink basis-0 text-slate-800 text-sm font-medium leading-tight"
                    >
                        Pay with points
                    </h5>
                </div>
                <PropertyCardBookingInfo
                    :propertyProgram="propertyProgramComputed"
                    :perNightRate="props.infoWindowContent.min_points"
                    layout="vertical"
                    v-if="mapType === 'search'"
                    :urlParams="{
                        name: 'HotelProperty',
                        params: { slug: props.infoWindowContent.slug },
                        query: { check_in: startDate, check_out: endDate },
                    }"
                />
                <PointsInfoCard
                    v-if="mapType === 'favorites' || mapType === 'property'"
                    :property="infoWindowContent"
                />
            </div>
        </div>
    </div>
</template>
<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { useCalendarStore } from '@/store/calendar';
import { useHotelsStore } from '@/store/hotels';
import PropertyCardBookingInfo from '@/components/hotels/search/property-card/property-card-booking-info.vue';
import PointsInfoCard from '@/components/hotels/search/points-info-card/points-info-card.vue';
import Fav from '@/components/hotels/property/fav.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Filters from '@/filters';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    infoWindowContent: Object,
});

const router = useRouter();

const calendarStore = useCalendarStore();
const hotelsStore = useHotelsStore();
const { stayLength, mapType } = storeToRefs(hotelsStore);
const { setShowOnMap, setMapType } = hotelsStore;
const { startDate, endDate } = storeToRefs(calendarStore);
const handleRouteToProperty = () => {
    let url;
    switch (mapType.value) {
        case 'search': {
            url = `/hotels/properties/${props.infoWindowContent.slug}?check_in=${startDate.value}&check_out=${endDate.value}`;
            break;
        }
        case 'favorites': {
            url = `/hotels/properties/${props.infoWindowContent.slug}`;
            break;
        }
        default:
            url = '/';
    }
    setShowOnMap(false);
    setMapType('search');
    if (mapType.value !== 'property') {
        router.push(url);
    }
};

const propertyImage = computed(() => {
    if (mapType.value === 'property') {
        return props.infoWindowContent.image_location;
    } else {
        return props.infoWindowContent.image.url;
    }
});

const propertyProgramComputed = computed(() => {
    if (mapType.value !== 'property') {
        return props.infoWindowContent.propertyProgram;
    } else {
        return hotelsStore.pointsProgram;
    }
});

// const startDate = computed(() => calendarStore.startDate);
// const endDate = computed(() => calendarStore.endDate);

const nightStayString = computed(() => {
    return `${stayLength.value} night${stayLength.value > 1 ? 's' : ''}`;
});

const cashPrice = computed(() => {
    return Filters.formatMoney(
        props.infoWindowContent.min_rate * stayLength.value
    );
});

const pointsPrice = computed(() => {
    return props.infoWindowContent.min_points * stayLength.value;
});
</script>
